import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import { ApiError } from '../../services/apiClient';
import spottoLogo from '../../assets/spotto-logo.png';

export const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [token, setToken] = useState('');

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setError(t('auth.validation.invalidResetLink'));
    } else {
      setToken(tokenParam);
    }
  }, [searchParams, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // Basic validation
    if (!email) {
      setError(t('auth.validation.emailRequired'));
      return;
    }

    if (!email.includes('@')) {
      setError(t('auth.validation.invalidEmail'));
      return;
    }

    if (!password) {
      setError(t('auth.validation.passwordRequired'));
      return;
    }

    if (password.length < 6) {
      setError(t('auth.validation.passwordTooShort'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.validation.passwordsDoNotMatch'));
      return;
    }

    if (!token) {
      setError(t('auth.validation.invalidResetLink'));
      return;
    }

    setIsLoading(true);

    try {
      const response = await authService.resetPassword({
        email,
        token,
        newPassword: password,
      });
      setSuccessMessage(response.message);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      if (err instanceof ApiError) {
        // Handle specific error codes
        if (err.data && typeof err.data === 'object' && 'error' in err.data) {
          const errorCode = (err.data as { error: string }).error;
          if (errorCode === 'INVALID_TOKEN') {
            setError(t('auth.validation.invalidOrExpiredToken'));
          } else if (errorCode === 'WEAK_PASSWORD') {
            setError(t('auth.validation.passwordTooShort'));
          } else {
            setError(err.message || t('auth.validation.unexpectedError'));
          }
        } else {
          setError(err.message || t('auth.validation.unexpectedError'));
        }
      } else {
        setError(t('auth.validation.unexpectedError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#23262b] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img
              src={spottoLogo}
              alt="Spotto Logo"
              className="h-20 w-auto"
              style={{
                filter:
                  "drop-shadow(0 6px 14px rgba(0, 0, 0, 0.45)) drop-shadow(0 0 28px rgba(77, 99, 172, 0.50))",
              }}
            />
          </div>
          <h1
            className="font-display text-3xl font-semibold text-white mb-2"
            style={{
              textShadow: "0 2px 10px rgba(0, 0, 0, 0.45)",
              letterSpacing: "-0.02em",
            }}
          >
            {t('app.name')}
          </h1>
          <p className="text-[#9aa3b2]">{t('app.subtitle')}</p>
        </div>

        {/* Reset Password Form */}
        <div className="bg-[#2c3036] rounded-2xl border border-white/[0.08] p-8 shadow-xl">
          <h2 className="text-2xl font-bold text-white mb-2">{t('auth.resetPassword')}</h2>
          <p className="text-sm text-[#9aa3b2] mb-6">
            {t('auth.resetPasswordDescription')}
          </p>

          {error && (
            <div className="mb-6 p-4 bg-[#c06f57]/10 border border-[#c06f57]/30 rounded-lg">
              <p className="text-sm text-[#e1a08a]">{error}</p>
            </div>
          )}

          {successMessage && (
            <div className="mb-6 p-4 bg-[#5fa78a]/10 border border-[#5fa78a]/30 rounded-lg">
              <p className="text-sm text-[#86c5ad]">{successMessage}</p>
              <p className="text-xs text-[#86c5ad] mt-1">{t('auth.redirectingToLogin')}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Input */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-[#c5cde0] mb-2">
                {t('auth.email')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent transition-all"
                placeholder={t('auth.emailPlaceholder')}
                disabled={isLoading || !!successMessage}
                autoComplete="email"
              />
            </div>

            {/* New Password Input */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-[#c5cde0] mb-2">
                {t('auth.newPassword')}
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent transition-all"
                  placeholder={t('auth.newPasswordPlaceholder')}
                  disabled={isLoading || !!successMessage}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9aa3b2] hover:text-[#c5cde0] transition-colors"
                  disabled={isLoading || !!successMessage}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              <p className="text-xs text-[#9aa3b2] mt-1">{t('auth.passwordRequirements')}</p>
            </div>

            {/* Confirm Password Input */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-[#c5cde0] mb-2">
                {t('auth.confirmPassword')}
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent transition-all"
                  placeholder={t('auth.confirmPasswordPlaceholder')}
                  disabled={isLoading || !!successMessage}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9aa3b2] hover:text-[#c5cde0] transition-colors"
                  disabled={isLoading || !!successMessage}
                >
                  {showConfirmPassword ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || !!successMessage}
              className="w-full py-3 px-4 bg-[#4d63ac] hover:bg-[#5b71be] disabled:bg-[#4d63ac]/50 text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:ring-offset-2 focus:ring-offset-[#2c3036]"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('auth.resetting')}
                </span>
              ) : (
                t('auth.resetPassword')
              )}
            </button>
          </form>

          {/* Back to Login Link */}
          {!successMessage && (
            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="text-sm text-[#9aa3b2] hover:text-[#c5cde0] transition-colors inline-flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                {t('auth.backToLogin')}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
